import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { 
  Server, Globe, Database, Smartphone, Cpu, ArrowLeft, Edit, Trash2, 
  Shield, AlertTriangle, Activity, Clock, CheckCircle, ExternalLink,
  FileText, Scan, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    fetchAssetData();
  }, [id]);

  const fetchAssetData = async () => {
    try {
      const [assetRes, vulnRes] = await Promise.all([
        api.get(`/assets/${id}`),
        api.get(`/assets/${id}/vulnerabilities`).catch(() => ({ data: [] }))
      ]);
      setAsset(assetRes.data);
      setVulnerabilities(vulnRes.data);
      setEditData({
        name: assetRes.data.name,
        type: assetRes.data.type,
        url: assetRes.data.url || '',
        ip_address: assetRes.data.ip_address || '',
        criticality: assetRes.data.criticality || 'medium'
      });
    } catch (error) {
      toast.error('Failed to fetch asset details');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/assets/${id}`, editData);
      toast.success('Asset updated successfully');
      setEditDialogOpen(false);
      fetchAssetData();
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/${id}`);
      toast.success('Asset deleted successfully');
      navigate('/assets');
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await api.post(`/assets/${id}/scan`);
      toast.success('Vulnerability scan initiated');
      setTimeout(() => {
        fetchAssetData();
        setScanning(false);
        toast.success('Scan completed');
      }, 3000);
    } catch (error) {
      toast.error('Failed to initiate scan');
      setScanning(false);
    }
  };

  const getAssetIcon = (type) => {
    const icons = { web: Globe, api: Server, mobile: Smartphone, infrastructure: Cpu, database: Database };
    return icons[type] || Server;
  };

  const getTypeColor = (type) => {
    const colors = {
      web: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      api: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      mobile: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
      infrastructure: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      database: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[type] || colors.web;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return colors[severity] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
  };

  const getCriticalityColor = (criticality) => {
    const colors = {
      critical: 'text-red-400',
      high: 'text-orange-400',
      medium: 'text-yellow-400',
      low: 'text-green-400',
    };
    return colors[criticality] || 'text-zinc-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading asset details...</div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Server className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400 font-inter">Asset not found</p>
        <Button onClick={() => navigate('/assets')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Assets
        </Button>
      </div>
    );
  }

  const Icon = getAssetIcon(asset.type);
  const vulnStats = {
    total: vulnerabilities.length,
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length,
    open: vulnerabilities.filter(v => v.status === 'open').length,
  };

  return (
    <div data-testid="asset-detail-container" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/assets')}
            className="text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${getTypeColor(asset.type).split(' ')[0]}`}>
            <Icon className={`w-7 h-7 ${getTypeColor(asset.type).split(' ')[1]}`} />
          </div>
          <div>
            <h1 className="font-rajdhani font-bold text-3xl text-white text-shadow-neon">
              {asset.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getTypeColor(asset.type)}`}>
                {asset.type}
              </span>
              <span className={`text-sm ${getCriticalityColor(asset.criticality || 'medium')}`}>
                {(asset.criticality || 'medium').charAt(0).toUpperCase() + (asset.criticality || 'medium').slice(1)} Criticality
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="bg-cyan-600 hover:bg-cyan-500 font-rajdhani"
          >
            {scanning ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Scanning...
              </>
            ) : (
              <>
                <Scan className="w-4 h-4 mr-2" />
                Run Scan
              </>
            )}
          </Button>

          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/10 text-zinc-300 hover:bg-white/5">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Edit Asset</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEdit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Name</Label>
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Type</Label>
                  <Select value={editData.type} onValueChange={(v) => setEditData({ ...editData, type: v })}>
                    <SelectTrigger className="bg-zinc-950/50 border-white/10 text-white mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="web">Web Application</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="mobile">Mobile App</SelectItem>
                      <SelectItem value="infrastructure">Infrastructure</SelectItem>
                      <SelectItem value="database">Database</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">URL</Label>
                  <Input
                    value={editData.url}
                    onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">IP Address</Label>
                  <Input
                    value={editData.ip_address}
                    onChange={(e) => setEditData({ ...editData, ip_address: e.target.value })}
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    placeholder="192.168.x.x"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Criticality</Label>
                  <Select value={editData.criticality} onValueChange={(v) => setEditData({ ...editData, criticality: v })}>
                    <SelectTrigger className="bg-zinc-950/50 border-white/10 text-white mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500">
                  Save Changes
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button 
            variant="destructive" 
            onClick={() => setDeleteDialogOpen(true)}
            className="bg-red-600 hover:bg-red-500"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <Shield className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
          <p className="text-2xl font-rajdhani font-bold text-white">{vulnStats.total}</p>
          <p className="text-xs text-zinc-500">Total Vulns</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-red-500">
          <p className="text-2xl font-rajdhani font-bold text-red-400">{vulnStats.critical}</p>
          <p className="text-xs text-zinc-500">Critical</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-orange-500">
          <p className="text-2xl font-rajdhani font-bold text-orange-400">{vulnStats.high}</p>
          <p className="text-xs text-zinc-500">High</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-yellow-500">
          <p className="text-2xl font-rajdhani font-bold text-yellow-400">{vulnStats.medium}</p>
          <p className="text-xs text-zinc-500">Medium</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-green-500">
          <p className="text-2xl font-rajdhani font-bold text-green-400">{vulnStats.low}</p>
          <p className="text-xs text-zinc-500">Low</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-red-400">
          <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-rajdhani font-bold text-red-300">{vulnStats.open}</p>
          <p className="text-xs text-zinc-500">Open Issues</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/10">
          <TabsTrigger value="details" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400">
            <FileText className="w-4 h-4 mr-2" />
            Details
          </TabsTrigger>
          <TabsTrigger value="vulnerabilities" className="data-[state=active]:bg-red-600/20 data-[state=active]:text-red-400">
            <Shield className="w-4 h-4 mr-2" />
            Vulnerabilities ({vulnStats.total})
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-400">
            <History className="w-4 h-4 mr-2" />
            Scan History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="glass rounded-2xl p-6">
              <h3 className="font-rajdhani font-semibold text-lg text-white mb-4">Asset Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Asset ID</span>
                  <span className="text-white font-mono text-sm">{asset.id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Name</span>
                  <span className="text-white">{asset.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Type</span>
                  <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getTypeColor(asset.type)}`}>
                    {asset.type}
                  </span>
                </div>
                {asset.url && (
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-zinc-500">URL</span>
                    <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                      {asset.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {asset.ip_address && (
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-zinc-500">IP Address</span>
                    <span className="text-white font-mono">{asset.ip_address}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Status</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    asset.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {asset.status || 'active'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Discovered</span>
                  <span className="text-white text-sm">{new Date(asset.discovered_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-zinc-500">Last Scanned</span>
                  <span className="text-white text-sm">
                    {asset.last_scanned ? new Date(asset.last_scanned).toLocaleString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <h3 className="font-rajdhani font-semibold text-lg text-white mb-4">Risk Assessment</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-zinc-500">Criticality Level</span>
                  <span className={`text-lg font-semibold ${getCriticalityColor(asset.criticality || 'medium')}`}>
                    {(asset.criticality || 'medium').toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-zinc-500">Risk Score</span>
                  <span className={`text-2xl font-rajdhani font-bold ${
                    vulnStats.critical > 0 ? 'text-red-400' : 
                    vulnStats.high > 0 ? 'text-orange-400' : 
                    vulnStats.medium > 0 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {Math.min(100, vulnStats.critical * 25 + vulnStats.high * 15 + vulnStats.medium * 5 + vulnStats.low * 2)}
                  </span>
                </div>
                <div className="pt-4">
                  <p className="text-zinc-500 text-sm mb-2">Vulnerability Distribution</p>
                  <div className="h-4 rounded-full overflow-hidden bg-zinc-800 flex">
                    {vulnStats.critical > 0 && (
                      <div 
                        className="bg-red-500 h-full" 
                        style={{ width: `${(vulnStats.critical / vulnStats.total) * 100}%` }}
                      />
                    )}
                    {vulnStats.high > 0 && (
                      <div 
                        className="bg-orange-500 h-full" 
                        style={{ width: `${(vulnStats.high / vulnStats.total) * 100}%` }}
                      />
                    )}
                    {vulnStats.medium > 0 && (
                      <div 
                        className="bg-yellow-500 h-full" 
                        style={{ width: `${(vulnStats.medium / vulnStats.total) * 100}%` }}
                      />
                    )}
                    {vulnStats.low > 0 && (
                      <div 
                        className="bg-green-500 h-full" 
                        style={{ width: `${(vulnStats.low / vulnStats.total) * 100}%` }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="vulnerabilities" className="mt-5">
          <div className="glass rounded-2xl p-5">
            <h3 className="font-rajdhani font-semibold text-lg text-white mb-4">Associated Vulnerabilities</h3>
            {vulnerabilities.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
                <p className="text-zinc-400">No vulnerabilities found for this asset</p>
                <p className="text-zinc-500 text-sm mt-2">Run a scan to check for vulnerabilities</p>
              </div>
            ) : (
              <div className="space-y-3">
                {vulnerabilities.map((vuln) => (
                  <div 
                    key={vuln.id}
                    className="glass-hover p-4 rounded-xl border border-white/10 hover:border-indigo-500/30 transition-all cursor-pointer"
                    onClick={() => navigate(`/ai-insights/${vuln.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getSeverityColor(vuln.severity)}`}>
                            {vuln.severity}
                          </span>
                          {vuln.cve_id && (
                            <span className="text-zinc-500 text-xs font-mono">{vuln.cve_id}</span>
                          )}
                        </div>
                        <p className="text-white font-inter">{vuln.title}</p>
                        <p className="text-zinc-500 text-xs mt-1 line-clamp-1">{vuln.description}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs ${
                          vuln.status === 'open' ? 'text-red-400' : 
                          vuln.status === 'closed' ? 'text-green-400' : 'text-yellow-400'
                        }`}>
                          {vuln.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <div className="glass rounded-2xl p-5">
            <h3 className="font-rajdhani font-semibold text-lg text-white mb-4">Scan History</h3>
            <div className="space-y-3">
              {[
                { date: new Date().toISOString(), type: 'Vulnerability Scan', findings: vulnStats.total, status: 'completed' },
                { date: new Date(Date.now() - 86400000 * 7).toISOString(), type: 'Security Assessment', findings: vulnStats.total - 2, status: 'completed' },
                { date: new Date(Date.now() - 86400000 * 14).toISOString(), type: 'Initial Discovery', findings: 0, status: 'completed' },
              ].map((scan, idx) => (
                <div key={idx} className="glass-hover p-4 rounded-xl border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-600/20 border border-emerald-500/30 rounded-lg flex items-center justify-center">
                        <Activity className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-inter">{scan.type}</p>
                        <p className="text-zinc-500 text-xs">{new Date(scan.date).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-rajdhani font-bold">{scan.findings} findings</p>
                      <span className="text-xs text-emerald-400">{scan.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Asset?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete "{asset.name}" and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-500">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
